import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Menu, X, Globe, User, LogOut } from 'lucide-react';
import { useAuthStore } from '@/lib/store';
import { LANGUAGES } from '@/lib/constants';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-lg shadow-brand-500/20">
              <span className="text-white font-bold text-lg">F</span>
            </div>
            <div className="leading-tight">
              <div className="font-bold text-navy-800 text-lg">FormMitra</div>
              <div className="text-[10px] text-gray-500 -mt-0.5">AI Form Auto-Fill</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/jobs">{t('nav.jobs')}</NavLink>
            <NavLink to="/pricing">{t('nav.pricing')}</NavLink>
            {user ? (
              <>
                <NavLink to="/dashboard">{t('nav.dashboard')}</NavLink>
                <NavLink to="/documents">{t('nav.documents')}</NavLink>
              </>
            ) : null}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase hidden sm:inline">
                  {i18n.language}
                </span>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-20">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => changeLang(l.code)}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-brand-50 flex items-center gap-2 ${
                          i18n.language === l.code ? 'bg-brand-50 text-brand-700 font-semibold' : ''
                        }`}
                      >
                        <span>{l.flag}</span> {l.nativeName}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/profile" className="p-2 hover:bg-gray-100 rounded-lg">
                  <User className="w-4 h-4" />
                </Link>
                <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-lg">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:inline-flex btn-primary text-sm py-2 px-4">
                {t('nav.login')}
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setOpen(!open)}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <nav className="md:hidden border-t border-gray-100 py-2 space-y-1">
            <MobileLink to="/jobs" onClick={() => setOpen(false)}>{t('nav.jobs')}</MobileLink>
            <MobileLink to="/pricing" onClick={() => setOpen(false)}>{t('nav.pricing')}</MobileLink>
            {user ? (
              <>
                <MobileLink to="/dashboard" onClick={() => setOpen(false)}>{t('nav.dashboard')}</MobileLink>
                <MobileLink to="/documents" onClick={() => setOpen(false)}>{t('nav.documents')}</MobileLink>
                <MobileLink to="/profile" onClick={() => setOpen(false)}>{t('nav.profile')}</MobileLink>
                <button
                  onClick={() => { handleLogout(); setOpen(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <MobileLink to="/login" onClick={() => setOpen(false)}>{t('nav.login')}</MobileLink>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-brand-600 hover:bg-brand-50/50 rounded-lg transition-colors"
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-700 rounded-lg"
    >
      {children}
    </Link>
  );
}
